import { effect, Injectable, signal } from '@angular/core';
import { CategoriesRepository } from '../../../domain/repositories/categories.repository';
import { Result } from '../../../domain/models/http-response.model';
import { HttpClientAdapter } from '../../../domain/adapters/http.adapter';
import { lastValueFrom } from 'rxjs';
import {
  CategoryItemModel,
  CategoryModel,
} from '../../../domain/models/category.model';
import {
  GetCategoriesResponse,
  GetCategoryResponse,
} from './categories.responses';
import { CategoryItemEntityMapper } from './mappers/category-item.mapper';
import { CategoryEntityMapper } from './mappers/category.mapper';
import { StorageAdapter } from '../../../domain/adapters/storage.adapter';

const CATEGORIES_KEY = 'categories';
@Injectable()
export class CategoriesRepositoryImpl implements CategoriesRepository {
  private _categories = signal<CategoryItemModel[]>(
    JSON.parse(this.storage.getBlocking<string | null>(CATEGORIES_KEY) || '[]') // get default value from cache
  );
  categories = this._categories.asReadonly();

  constructor(
    private httpClient: HttpClientAdapter,
    private storage: StorageAdapter
  ) {
    this.loadCategories();
  }

  cacheCategoriesEffect = effect(() => {
    this.storage.set<string>(CATEGORIES_KEY, JSON.stringify(this.categories()));
  });

  async getCategories(): Promise<Result<CategoryItemModel[], Error>> {
    const result = await lastValueFrom(
      this.httpClient.request<GetCategoriesResponse>({
        method: 'GET',
        url: `/categories`,
      })
    );
    if (result.isErr()) return result;

    const entityMapper = new CategoryItemEntityMapper();
    const categories = result
      .unwrap()
      .categories.map((entity) => entityMapper.mapFrom(entity));

    return new Result(categories, null);
  }

  async loadCategories(): Promise<Result<unknown, Error> | undefined> {
    const result = await lastValueFrom(
      this.httpClient.request<GetCategoriesResponse>({
        method: 'GET',
        url: `/categories`,
      })
    );
    if (result.isErr()) return result;

    const entityMapper = new CategoryItemEntityMapper();
    const categories = result
      .unwrap()
      .categories.map((entity) => entityMapper.mapFrom(entity));
    this._categories.set(categories);

    return undefined;
  }

  async getCategory(idOrSlug: string): Promise<Result<CategoryModel, Error>> {
    const result = await lastValueFrom(
      this.httpClient.request<GetCategoryResponse>({
        method: 'GET',
        url: `/categories/${idOrSlug}`,
      })
    );
    if (result.isErr()) return result;

    const entityMapper = new CategoryEntityMapper();
    const category = entityMapper.mapFrom(result.unwrap().category);

    return new Result(category, null);
  }
}
